import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tile,
} from "@carbon/react";
import { MaterialType, OrderResponse } from "../../types";
import "./bid.scss";

type MaterialsOverviewTileProps = {
  order?: OrderResponse;
};

/**
 * The single-order "Materials Overview" card (`BuyerBid.tsx`, Task 7) — one order, one material
 * line rendered as a real Carbon `Table` (replacing the invented bordered flex card): EAN + name,
 * plus quantity/unit UNLESS the material is a SERVICES-type item, in which case the order's
 * `purpose` renders instead (same `MaterialType.SERVICES` branch the pre-Carbon version had).
 */
export const MaterialsOverviewTile = ({
  order,
}: MaterialsOverviewTileProps) => {
  const { t } = useTranslation();
  const isServices = order?.material?.category?.type === MaterialType.SERVICES;

  return (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">{t("tenders.data.materialsOverview")}</h3>
      <Table size="lg" useZebraStyles className="bid-materials__table">
        <TableHead>
          <TableRow>
            <TableHeader>{t("tenders.form.material")}</TableHeader>
            <TableHeader>
              {isServices
                ? t("purchaseRequests.items.columns.purpose")
                : t("tenders.quantity")}
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <span className="bid-materials__ean">{order?.material?.ean}</span>
              {" - "}
              {order?.material?.globalMaterialName}
            </TableCell>
            <TableCell>
              {isServices
                ? order?.purpose
                : `${order?.quantity ?? ""} ${order?.measurementUnit ?? ""}`}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Tile>
  );
};

type JointMaterialsOverviewTileProps = {
  orders?: OrderResponse[];
};

/**
 * The JOINT-order "Materials Overview" card (`JointBuyerBid.tsx`, Task 7) — one Carbon `Table` row
 * per order line in the group, each with EAN + name + quantity/unit (the pre-Carbon joint version
 * never had the SERVICES/purpose branch the single-order one has, so it stays out here too).
 */
export const JointMaterialsOverviewTile = ({
  orders,
}: JointMaterialsOverviewTileProps) => {
  const { t } = useTranslation();

  return (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">{t("tenders.data.materialsOverview")}</h3>
      <Table size="lg" useZebraStyles className="bid-materials__table">
        <TableHead>
          <TableRow>
            <TableHeader>{t("tenders.form.material")}</TableHeader>
            <TableHeader>{t("tenders.quantity")}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders?.map((orderItem) => (
            <TableRow key={orderItem.id}>
              <TableCell>
                <span className="bid-materials__ean">
                  {orderItem.material.ean}
                </span>
                {" - "}
                {orderItem.material.globalMaterialName}
              </TableCell>
              <TableCell>
                {orderItem.quantity} {orderItem.measurementUnit}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Tile>
  );
};
